// No 'use client': server-rendered under server parents, hydrates under client ones.
import Link from 'next/link';
import Image from 'next/image';
import { useLocale } from 'next-intl';
import { BlogPostPreview } from '../../types/blog';
import { localizeFullPathFor } from '../../i18n/localizePath';
import type { Locale } from '../../i18n/config';
import { formatMediumDate, formatRelativeTime } from '../../utils/dateFormat';
import fullLogoBlack from '../../assets/images/full-logo-black.svg';
import { SITE_CONFIG } from '@/constants';
import styles from './Blog.module.css';

interface BlogCardProps {
  post: BlogPostPreview;
  variant?: 'small' | 'large';
  /** Only for cards in the initial viewport (e.g. the blog listing's featured
   * card). Everywhere else the image lazy-loads - an unconditional preload
   * here put 3 below-fold thumbnails ahead of the homepage LCP hero. */
  eagerImage?: boolean;
}

export function BlogCard({ post, variant = 'small', eagerImage = false }: BlogCardProps) {
  const locale = useLocale();
  const localizeFullPath = (path: string) => localizeFullPathFor(locale as Locale, path);
  const isLarge = variant === 'large';
  const publishedDate = new Date(post.publishedAt || post.createdAt);

  return (
    <article className={`${styles.blogCard} ${isLarge ? styles.blogCardLarge : styles.blogCardSmall}`}>
      <Link href={localizeFullPath(`/blog/${post.slug}`)} className={styles.blogCardLink}>
        <div className={`${styles.blogCardImageWrap} ${isLarge ? styles.blogCardImageLarge : styles.blogCardImageSmall}`}>
          <div className={styles.blogCardImageInner}>
            <Image
              src={post.featuredImage || fullLogoBlack}
              alt={post.title}
              fill
              unoptimized
              loading={eagerImage ? 'eager' : 'lazy'}
              priority={eagerImage}
              style={{ objectFit: 'contain' }}
            />
          </div>
        </div>
        
        <div className={`${styles.blogCardContent} ${isLarge ? styles.blogCardContentLarge : ''}`}>
          {post.tags.length > 0 && (
            <div className={`${styles.blogCardTags} ${isLarge ? styles.blogCardTagsLarge : ''}`}>
              {post.tags.slice(0, 3).map((tag) => (
                <span key={tag} className={`${styles.blogCardTag} ${isLarge ? styles.blogCardTagLarge : ''}`}>
                  {tag}
                </span>
              ))}
            </div>
          )}
          
          <h3 className={`${styles.blogCardTitle} ${isLarge ? styles.blogCardTitleLarge : styles.blogCardTitleSmall}`}>
            {post.title}
          </h3>
          
          {post.excerpt && (
            <p className={`${styles.blogCardExcerpt} ${isLarge ? styles.blogCardExcerptLarge : styles.blogCardExcerptSmall}`}>
              {post.excerpt}
            </p>
          )}
          
          <div className={`${styles.blogCardMeta} ${isLarge ? styles.blogCardMetaLarge : styles.blogCardMetaSmall}`}>
            <span className={`${styles.blogCardAuthor} ${isLarge ? styles.blogCardAuthorLarge : ''}`}>
              {post.authorProfile?.name || SITE_CONFIG.name}
            </span>
            {/* relative time depends on "now" - SSR and hydrate can differ */}
            <span suppressHydrationWarning>
              {formatMediumDate(publishedDate, locale)}
              {' · '}
              {formatRelativeTime(publishedDate, locale)}
            </span>
          </div>
        </div>
      </Link>
    </article>
  );
}
